import React, { useEffect, useState } from "react";
import "../My Projects/my_projects.css";
import { toast } from "react-toastify";
import {
  getAllAssignedProjects,
  getReport,
} from "../../service/project_service";
import { useNavigate } from "react-router-dom";
import {
  Layers,
  Calendar,
  Clock,
  CheckCircle2,
  ArrowRight
} from "lucide-react";
import CustomLoadingindicator from "../../components/custom_loading";

const MyProjects = () => {
  const [projects, setProjects] = useState([]);
  const [workReports, setWorkReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const getProjectStats = (project) => {
    const projectReports = workReports.filter(
      (report) =>
        report.projectid === project.projectid ||
        report.projectname === project.projectname,
    );

    // Calculate total minutes correctly handling HH.mm format (e.g., 3.20 = 3h 20m)
    const totalMinutes = projectReports.reduce((total, report) => {
      const val = Number(report.workinghours) || 0;
      const hours = Math.floor(val);
      const minutes = Math.round((val - hours) * 100);
      return total + hours * 60 + minutes;
    }, 0);

    // Calculate total unique working days
    const uniqueDays = new Set(projectReports.map((r) => r.workdate)).size;

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return {
      formattedHours: `${hours} hr: ${minutes} mins`,
      formattedDays: `${uniqueDays} ${uniqueDays > 1 ? "days" : "day"}`,
    };
  };

  const getAllMyProjectsData = async () => {
    try {
      const [projectsResponse, reportsResponse] = await Promise.all([
        getAllAssignedProjects(),
        getReport({
          startDate: "",
          endDate: "",
          projectid: 0,
        }),
      ]);

      if (projectsResponse?.status === "success" && projectsResponse.data) {
        setProjects(projectsResponse.data);
      } else {
        toast.error(projectsResponse?.message || "Failed to fetch projects");
      }

      if (reportsResponse?.status === "success") {
        setWorkReports(reportsResponse.data || []);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    getAllMyProjectsData();
  }, []);

  if (loading) {
    return <CustomLoadingindicator />;
  }

  return (
    <div className="projects-container">
      <div className="page-header">
        <h2>My Assigned Projects</h2>
      </div>

      {projects.length === 0 ? (
        <div className="empty-projects-state">
          <img
            src="https://cdn-icons-png.flaticon.com/128/14782/14782500.png"
            alt="No-Project"
            height={50}
            width={50}
            style={{ marginBottom: "10px" }}
          />
          <h3>No Projects Assigned Yet</h3>
        </div>
      ) : (
        <div className="projects-grid">
          {projects
            .map((project) => {
              const stats = getProjectStats(project);
              const firstModule = project.modules?.[0] || {};
              const projectInitial = project.projectname?.charAt(0) || "P";

              return (
                <div
                  className="project-card-premium"
                  key={project.projectid}
                  onClick={() =>
                    navigate("/project-detail", {
                      state: { project, totalStats: stats },
                    })
                  }
                >
                  <div className="card-header-premium">
                    <div className="project-brand-section">
                      <div className="project-avatar-circle">
                        {projectInitial}
                      </div>
                      <div className="project-title-meta">
                        <h3>{project?.projectname || "Untitled Project"}</h3>
                        <div className="active-modules-pill">
                          <Layers size={10} />
                          <span>{project.modules?.length || 0} Modules</span>
                        </div>
                        {project.description && (
                          <p className="project-card-description">{project.description}</p>
                        )}
                      </div>
                    </div>
                    <div className={`priority-badge-modern ${(firstModule.tasks?.[0]?.priority || "medium").toLowerCase()}`}>
                      {firstModule.tasks?.[0]?.priority || "Medium"}
                    </div>
                  </div>

                  <div className="card-body-premium">
                    <div className="assignments-track">
                      <label>Assignments</label>
                      <p>
                        {project.modules?.map(m => {
                          const tasksStr = m.tasks?.map(t => t.taskname).filter(Boolean).join(", ");
                          return tasksStr ? `${m.modulename} (${tasksStr})` : m.modulename;
                        }).join(" | ") || "General Assignment"}
                      </p>
                    </div>

                    <div className="card-metrics-grid">
                      <div className="metric-item-sleek">
                        <div className="metric-icon-box">
                          <Calendar size={14} />
                        </div>
                        <div className="metric-content-box">
                          <span>Assigned</span>
                          <p>
                            {project.assigneddate
                              ? new Date(project.assigneddate).toLocaleDateString("en-GB")
                              : "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="metric-item-sleek">
                        <div className="metric-icon-box deadline">
                          <Clock size={14} />
                        </div>
                        <div className="metric-content-box">
                          <span>Latest Deadline</span>
                          <p className="deadline-text">
                            {project.deadlinedate
                              ? new Date(project.deadlinedate).toLocaleDateString("en-GB")
                              : "No Deadline"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="performance-summary-strip">
                      <div className="summary-col">
                        <span className="summary-label">Worked Days</span>
                        <span className="summary-val">{stats.formattedDays}</span>
                      </div>
                      <div className="divider-line"></div>
                      <div className="summary-col">
                        <span className="summary-label">Worked Hours</span>
                        <span className="summary-val">{stats.formattedHours}</span>
                      </div>
                    </div>
                  </div>

                  <div className="card-footer-premium">
                    <button
                      className="premium-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/add-workreports", {
                          state: { projectid: project?.projectid },
                        });
                      }}
                    >
                      <CheckCircle2 size={15} />
                      Log Work Progress
                    </button>
                    <div className="card-hover-arrow">
                      <ArrowRight size={16} />
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default MyProjects;
